import { BulkUploadModal, ValidationError, BulkUploadResult, LanguageColumnMapping } from '@/components/common/BulkUploadModal';
import { Asset, AssetStatus, AssetCategory } from '@/types/resources/asset';
import { useAuth } from '@/contexts/AuthContext';
import { addAssetsBulk } from '@/api/assets';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { getTemplateURL } from '@/templates/csv/templateLoader';
import { Modal, Progress, Button, Row, Col, Typography, Tag } from 'antd';
import { ToolOutlined, TagOutlined, CalendarOutlined, DollarOutlined } from '@ant-design/icons';
import { useState } from 'react';

const { Title, Text } = Typography;

interface AssetsBulkUploadModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

export function AssetsBulkUploadModal({ visible, onCancel, onSuccess }: AssetsBulkUploadModalProps) {
  const { firestoreUser, refreshWorkspaceStats } = useAuth();
  const { t, i18n } = useTranslation(['assets', 'common']);
  const currentLanguage = i18n.language as 'en' | 'es' | 'pt';
  
  // State for managing the flow
  const [currentStep, setCurrentStep] = useState<'upload' | 'preview' | 'creating'>('upload');
  const [processedData, setProcessedData] = useState<Array<{
    asset: Omit<Asset, 'id' | 'workspace' | 'activityRecords'>;
    name: string;
    category: string;
    status: string;
    purchaseDate: string;
    purchasePrice: string;
  }>>([]);

  // Sample data for template generation
  const sampleData: any[] = [];

  // Column definitions for the template (internationalized)
  const columns = [
    { key: 'name', title: t('assets:fields.name'), required: true },
    { key: 'category', title: t('assets:fields.category'), required: true },
    { key: 'subCategory', title: t('assets:fields.subCategory'), required: false },
    { key: 'status', title: t('assets:fields.status'), required: true },
    { key: 'location', title: t('assets:fields.location'), required: false },
    { key: 'purchaseDate', title: t('assets:fields.purchaseDate'), required: false },
    { key: 'purchasePrice', title: t('assets:fields.purchasePrice'), required: false },
    { key: 'blockName', title: t('assets:fields.blockName'), required: false },
    { key: 'floorName', title: t('assets:fields.floorName'), required: false },
    { key: 'notes', title: t('assets:fields.notes'), required: false }
  ];

  // Language-aware column mappings for CSV headers
  const columnMappings: LanguageColumnMapping = {
    en: {
      name: ['name', 'asset name', 'assetname', 'item name', 'itemname', 'equipment name'],
      category: ['category', 'cat', 'type', 'asset type', 'assettype'],
      subCategory: ['subcategory', 'sub category', 'sub_category', 'subtype', 'sub type'],
      status: ['status', 'condition', 'state'],
      location: ['location', 'place', 'position', 'where'],
      purchaseDate: ['purchasedate', 'purchase date', 'purchase_date', 'buy date', 'buydate', 'acquisition date'],
      purchasePrice: ['purchaseprice', 'purchase price', 'purchase_price', 'cost', 'price', 'value'],
      blockName: ['blockname', 'block name', 'block_name', 'building', 'block'],
      floorName: ['floorname', 'floor name', 'floor_name', 'level', 'floor'],
      notes: ['notes', 'comments', 'description', 'remarks']
    },
    es: {
      name: ['nombre', 'name', 'nombre del activo', 'nombredelactivo', 'nombre del artículo', 'nombredelarticulo'],
      category: ['categoría', 'categoria', 'cat', 'tipo', 'tipo de activo', 'tipodeactivo'],
      subCategory: ['subcategoría', 'subcategoria', 'sub categoría', 'sub_categoría', 'subtipo', 'sub tipo'],
      status: ['estado', 'condición', 'condicion', 'estado'],
      location: ['ubicación', 'ubicacion', 'lugar', 'posición', 'posicion', 'dónde'],
      purchaseDate: ['fechacompra', 'fecha de compra', 'fecha_compra', 'fecha compra', 'fechacompra', 'fecha adquisición'],
      purchasePrice: ['preciocompra', 'precio de compra', 'precio_compra', 'costo', 'precio', 'valor'],
      blockName: ['nombrebloque', 'nombre del bloque', 'nombre_bloque', 'edificio', 'bloque'],
      floorName: ['nombrepiso', 'nombre del piso', 'nombre_piso', 'nivel', 'piso'],
      notes: ['notas', 'comentarios', 'descripción', 'observaciones']
    },
    pt: {
      name: ['nome', 'name', 'nome do ativo', 'nomedoativo', 'nome do artigo', 'nomedoartigo'],
      category: ['categoria', 'cat', 'tipo', 'tipo de ativo', 'tipodeativo'],
      subCategory: ['subcategoria', 'sub categoria', 'sub_categoria', 'subtipo', 'sub tipo'],
      status: ['status', 'condição', 'condicao', 'estado'],
      location: ['localização', 'localizacao', 'lugar', 'posição', 'posicao', 'onde'],
      purchaseDate: ['datacompra', 'data de compra', 'data_compra', 'data compra', 'datacompra', 'data aquisição'],
      purchasePrice: ['precocompra', 'preço de compra', 'preco_compra', 'custo', 'preço', 'preco', 'valor'],
      blockName: ['nomebloco', 'nome do bloco', 'nome_bloco', 'edifício', 'bloco'],
      floorName: ['nomeandar', 'nome do andar', 'nome_andar', 'nível', 'andar'],
      notes: ['notas', 'comentários', 'descrição', 'observações']
    }
  };

  // Validation function for asset records
  const validateAssetRecord = (record: any, rowIndex: number): ValidationError[] => {
    const errors: ValidationError[] = [];

    // Required field validation
    if (!record.name || record.name.trim() === '') {
      errors.push({
        row: rowIndex,
        field: 'name',
        value: record.name,
        message: t('assets:bulkUpload.validation.nameRequired')
      });
    } else if (record.name.length > 100) {
      errors.push({
        row: rowIndex,
        field: 'name',
        value: record.name,
        message: t('assets:bulkUpload.validation.nameTooLong')
      });
    }

    if (!record.category || record.category.trim() === '') {
      errors.push({
        row: rowIndex,
        field: 'category',
        value: record.category,
        message: t('assets:bulkUpload.validation.categoryRequired')
      });
    } else if (!['structural', 'electrical', 'plumbing', 'maintenance', 'security', 'amenities', 'furniture', 'it'].includes(record.category.toLowerCase())) {
      errors.push({
        row: rowIndex,
        field: 'category',
        value: record.category,
        message: t('assets:bulkUpload.validation.categoryInvalid')
      });
    }

    if (!record.status || record.status.trim() === '') {
      errors.push({
        row: rowIndex,
        field: 'status',
        value: record.status,
        message: t('assets:bulkUpload.validation.statusRequired')
      });
    } else if (!['new', 'good', 'fair', 'poor', 'damaged', 'active', 'maintenance', 'retired', 'lost'].includes(record.status.toLowerCase())) {
      errors.push({
        row: rowIndex,
        field: 'status',
        value: record.status,
        message: t('assets:bulkUpload.validation.statusInvalid')
      });
    }

    // Optional field validation
    if (record.purchaseDate && record.purchaseDate.trim() !== '') {
      const purchaseDate = new Date(record.purchaseDate);
      if (isNaN(purchaseDate.getTime())) {
        errors.push({
          row: rowIndex,
          field: 'purchaseDate',
          value: record.purchaseDate,
          message: t('assets:bulkUpload.validation.purchaseDateInvalid')
        });
      }
    }

    if (record.purchasePrice && record.purchasePrice.trim() !== '') {
      const price = parseFloat(record.purchasePrice);
      if (isNaN(price) || price < 0) {
        errors.push({
          row: rowIndex,
          field: 'purchasePrice',
          value: record.purchasePrice,
          message: t('assets:bulkUpload.validation.purchasePriceInvalid')
        });
      }
    }

    return errors;
  };

  // Transform CSV record to Asset type
  const transformAssetRecord = (record: any): Omit<Asset, 'id' | 'workspace' | 'activityRecords'> => {
    return {
      name: record.name.trim(),
      category: record.category.toLowerCase() as AssetCategory,
      subCategory: record.subCategory?.trim(),
      status: record.status.toLowerCase() as AssetStatus,
      location: record.location?.trim(),
      purchaseDate: record.purchaseDate ? new Date(record.purchaseDate) : undefined,
      purchasePrice: record.purchasePrice ? {
        amount: parseFloat(record.purchasePrice),
        currency: 'USD' // Default currency
      } : undefined,
      block: record.blockName ? {
        id: '', // Will be filled by the system
        name: record.blockName.trim()
      } : undefined,
      floor: record.floorName ? {
        slug: record.floorName.toLowerCase().replace(/\s+/g, '-'),
        name: record.floorName.trim()
      } : undefined,
      notes: record.notes || ''
    };
  };

  // Handle bulk upload confirmation - shows preview first
  const handleBulkUploadConfirm = async (result: BulkUploadResult<Omit<Asset, 'id' | 'workspace' | 'activityRecords'>>) => {
    if (!firestoreUser) {
      message.error('User not authenticated');
      return;
    }

    try {
      // Process the data to show preview
      const processedData = result.validRecords.map(assetData => ({
        asset: assetData,
        name: assetData.name,
        category: assetData.category,
        status: assetData.status,
        purchaseDate: assetData.purchaseDate?.toLocaleDateString() || 'N/A',
        purchasePrice: assetData.purchasePrice ? `$${assetData.purchasePrice.amount}` : 'N/A'
      }));

      setProcessedData(processedData);
      setCurrentStep('preview');

    } catch (error) {
      console.error('Error processing data:', error);
      message.error('Error processing data. Please try again.');
    }
  };

  // Handle the actual creation after confirmation
  const handleConfirmCreation = async () => {
    if (!firestoreUser || processedData.length === 0) {
      message.error('No data to process');
      return;
    }

    try {
      setCurrentStep('creating');
      
      let totalUploaded = 0;
      const totalRecords = processedData.length;
      
      // Show progress modal
      const progressModal = Modal.info({
        title: 'Creating Assets',
        content: (
          <div>
            <Progress 
              percent={0} 
              status="active" 
              format={(percent) => `${totalUploaded}/${totalRecords} (${percent}%)`}
            />
            <p>Creating assets...</p>
          </div>
        ),
        okButtonProps: { style: { display: 'none' } }
      });

      // Prepare assets data for bulk upload
      const assetsData = processedData.map(data => data.asset);

      // Create assets in bulk
      const result = await addAssetsBulk(assetsData, firestoreUser, (uploaded, total) => {
        const percent = Math.round((uploaded / total) * 100);
        progressModal.update({
          content: (
            <div>
              <Progress 
                percent={percent} 
                status="active" 
                format={(percent) => `${uploaded}/${total} (${percent}%)`}
              />
              <p>Creating assets: {uploaded} of {total}</p>
            </div>
          )
        });
      });

      totalUploaded = result.successCount;

      // Refresh workspace stats
      try {
        await refreshWorkspaceStats();
      } catch (error) {
        console.warn('Failed to refresh workspace stats:', error);
      }

      progressModal.destroy();
      message.success(`Successfully created ${totalUploaded} assets`);
      
      onSuccess();
    } catch (error) {
      console.error('Progress-aware upload error:', error);
      message.error('Error creating assets. Please try again.');
    }
  };

  // Handle modal cancel
  const handleModalCancel = () => {
    setCurrentStep('upload');
    setProcessedData([]);
    onCancel();
  };

  // Render different content based on current step
  const renderContent = () => {
    switch (currentStep) {
      case 'upload':
        return (
          <BulkUploadModal
            visible={visible}
            onCancel={handleModalCancel}
            onConfirm={handleBulkUploadConfirm}
            title={t('assets:actions.bulkUpload')}
            templateUrl={getTemplateURL(currentLanguage, 'assets')}
            templateFileName={`assets_template_${currentLanguage}.csv`}
            validateRecord={validateAssetRecord}
            transformRecord={transformAssetRecord}
            columns={columns}
            sampleData={sampleData}
            accept=".csv"
            maxFileSize={5}
            language={currentLanguage}
            columnMappings={columnMappings}
          />
        );

      case 'preview':
        return (
          <Modal
            title={t('assets:bulkUpload.title')}
            open={visible}
            onCancel={handleModalCancel}
            footer={[
              <Button key="cancel" onClick={handleModalCancel}>
                {t('common:actions.cancel')}
              </Button>,
              <Button key="confirm" type="primary" onClick={handleConfirmCreation}>
                {t('common:actions.confirm')}
              </Button>
            ]}
            width={800}
          >
            <div style={{ marginTop: 16 }}>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <Title level={3} style={{ color: '#1890ff', marginBottom: '8px' }}>
                  {t('assets:bulkUpload.preview.title')}
                </Title>
                <Text style={{ color: '#666', fontSize: '14px' }}>
                  {t('assets:bulkUpload.preview.subtitle')}
                </Text>
              </div>

              {/* Summary Details */}
              <div style={{ 
                backgroundColor: '#fff', 
                padding: '20px', 
                borderRadius: '6px', 
                border: '1px solid #d9d9d9',
                marginBottom: '24px'
              }}>
                {processedData.map((data, index) => (
                  <div key={index} style={{ 
                    marginBottom: index < processedData.length - 1 ? '20px' : '0',
                    padding: '16px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #e9ecef'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                      <div style={{ 
                        width: '40px', 
                        height: '40px', 
                        backgroundColor: '#1890ff', 
                        borderRadius: '50%', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        marginRight: '16px'
                      }}>
                        <ToolOutlined style={{ color: '#fff', fontSize: '18px' }} />
                      </div>
                      <div>
                        <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
                          {data.name}
                        </Title>
                        <Text style={{ margin: 0, color: '#666', fontSize: '12px' }}>
                          {data.category} • {data.asset.location || 'No location'}
                        </Text>
                      </div>
                    </div>

                    <Row gutter={16}>
                      <Col span={6}>
                        <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f6ffed', borderRadius: '6px', border: '1px solid #b7eb8f' }}>
                          <Tag color="green" style={{ margin: 0 }}>
                            {data.status}
                          </Tag>
                          <div style={{ fontSize: '12px', color: '#52c41a', marginTop: '4px' }}>
                            Status
                          </div>
                        </div>
                      </Col>
                      <Col span={6}>
                        <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#e6f7ff', borderRadius: '6px', border: '1px solid #91d5ff' }}>
                          <TagOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
                          <div style={{ fontSize: '12px', color: '#1890ff', marginTop: '4px' }}>
                            {data.category}
                          </div>
                        </div>
                      </Col>
                      <Col span={6}>
                        <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#fff7e6', borderRadius: '6px', border: '1px solid #ffd591' }}>
                          <CalendarOutlined style={{ fontSize: '16px', color: '#fa8c16' }} />
                          <div style={{ fontSize: '12px', color: '#fa8c16', marginTop: '4px' }}>
                            {data.purchaseDate}
                          </div>
                        </div>
                      </Col>
                      <Col span={6}>
                        <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f0f0f0', borderRadius: '6px', border: '1px solid #d9d9d9' }}>
                          <DollarOutlined style={{ fontSize: '16px', color: '#666' }} />
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                            {data.purchasePrice}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                ))}

                {/* Confirmation Message */}
                <div style={{ 
                  marginTop: '20px', 
                  padding: '16px', 
                  backgroundColor: '#fff2e8', 
                  borderRadius: '6px', 
                  border: '1px solid #ffbb96',
                  textAlign: 'center'
                }}>
                  <Text style={{ margin: 0, color: '#d46b08', fontSize: '14px' }}>
                    {t('assets:bulkUpload.preview.confirmationMessage', {
                      count: processedData.length
                    })}
                  </Text>
                </div>
              </div>
            </div>
          </Modal>
        );

      case 'creating':
        return (
          <Modal
            title="Creating Assets..."
            open={visible}
            onCancel={handleModalCancel}
            footer={null}
            width={600}
          >
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Progress type="circle" percent={100} status="active" />
              <Text style={{ display: 'block', marginTop: '16px' }}>
                Please wait while the assets are being created...
              </Text>
            </div>
          </Modal>
        );

      default:
        return null;
    }
  };

  return renderContent();
}
